import { useState, useCallback, useRef, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams, useNavigate, generatePath } from 'react-router-dom';
import {
  Button,
  Box,
  Backdrop,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { AppLayout } from '@base/ui/layouts/app-layout';
import { AppHeader } from '@base/ui/components/app-header';
import { ErrorBanner } from '@base/ui/components/error-banner';
import { ProductInfoCard } from '../ticket-list/product-info-card';
import { DefectLinesList } from '../create-ticket/defect-lines-list';
import { NotesTextarea } from '../create-ticket/notes-textarea';
import { DetailSkeleton } from '../ticket-detail/detail-skeleton';
import { UnsavedChangesModal } from './unsaved-changes-modal';
import { useImageUpload } from '../create-ticket/use-image-upload';
import { useQueryClient } from '@tanstack/react-query';
import { useTicketDetail, useUpdateTicket, useDeleteDefectLine } from '@base/state';
import { invalidateTicketDetail } from '@base/state/cache-config';
import { useNavigationStore } from '../router/navigation-store';
import { useBackButton } from '../router/use-back-button';
import { useUnsavedChanges } from '../router/use-unsaved-changes';
import { ROUTES } from '../router/route-constants';
import { editTicketFormSchema, type CreateTicketFormData } from '@core/utils/validation';
import { triggerDeepLink } from '@core/utils/deep-link';
import type { DefectLineInput, ImageResponse, UpdateTicketRequest, UpdateDefectLineInput } from '@core/types/ticket';

type SubmitStep = 'idle' | 'validating' | 'uploading' | 'updating' | 'success' | 'error';

interface SubmitState {
  step: SubmitStep;
  progress: number;
  message: string;
}

export function EditPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const urlParams = useNavigationStore((state) => state.urlParams);
  const resolvedItemCode = useNavigationStore((state) => state.resolvedItemCode);
  const { goBack } = useBackButton();

  const { data: ticket, isLoading: isLoadingTicket, isError, error: fetchError, refetch } = useTicketDetail(ticketId || '');
  const queryClient = useQueryClient();
  const updateTicketMutation = useUpdateTicket();
  const imageUpload = useImageUpload();
  const cancelRequestedRef = useRef(false);
  const hasNavigatedRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // Track existing images per defect line (from API, not File objects)
  const [existingImagesMap, setExistingImagesMap] = useState<Map<number, ImageResponse[]>>(new Map());

  // Delete confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    apiDefectLineName?: string;
    formIndex: number;
    removeFn: () => void;
  } | null>(null);

  const deleteDefectLineMutation = useDeleteDefectLine();

  const [submitState, setSubmitState] = useState<SubmitState>({
    step: 'idle',
    progress: 0,
    message: '',
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const methods = useForm<CreateTicketFormData>({
    resolver: zodResolver(editTicketFormSchema),
    mode: 'onTouched',
    defaultValues: {
      defectLines: [
        {
          lineType: 'Machine',
          componentItemCode: null,
          isOtherComponent: false,
          otherComponentDescription: '',
          marketDefect: null,
          isOtherDefect: false,
          otherDefectDescription: '',
          marketDefectCause: null,
          isOtherCause: false,
          otherCauseDescription: '',
          apiDefectLineName: undefined,
          images: [],
        },
      ],
      notes: '',
    },
  });

  // Pre-populate form when ticket data arrives
  useEffect(() => {
    // Skip if we've already navigated away after a successful save
    if (hasNavigatedRef.current) return;
    // Only initialize once — subsequent resets would clear isSubmitted and hide validation errors
    if (hasInitializedRef.current) return;
    if (ticket) {
      hasInitializedRef.current = true;
      const existingMap = new Map<number, ImageResponse[]>();

      const defectLines = (ticket.defect_lines ?? []).map((line, index) => {
        // Store existing images separately
        if (line.images && line.images.length > 0) {
          existingMap.set(index, line.images);
        }

        return {
          lineType: line.line_type as 'Machine' | 'Component',
          componentItemCode: line.component_item_code || null,
          isOtherComponent: !!line.is_other_component,
          otherComponentDescription: line.other_component_description || '',
          marketDefect: line.market_defect || null,
          isOtherDefect: !!line.is_other_defect,
          otherDefectDescription: line.other_defect_description || '',
          marketDefectCause: line.market_defect_cause || null,
          isOtherCause: !!line.is_other_cause,
          otherCauseDescription: line.other_cause_description || '',
          apiDefectLineName: line.name, // Track API ID for delete
          images: [], // New images only (existing tracked separately)
        };
      });

      setExistingImagesMap(existingMap);

      methods.reset({
        defectLines: defectLines.length > 0 ? defectLines : [{
          lineType: 'Machine',
          componentItemCode: null,
          isOtherComponent: false,
          otherComponentDescription: '',
          marketDefect: null,
          isOtherDefect: false,
          otherDefectDescription: '',
          marketDefectCause: null,
          isOtherCause: false,
          otherCauseDescription: '',
          apiDefectLineName: undefined,
          images: [],
        }],
        notes: ticket.notes || '',
      });
    }
  }, [ticket, methods]);

  // Unsaved changes
  const { showModal, confirmNavigation, cancelNavigation } = useUnsavedChanges({
    isDirty: methods.formState.isDirty,
  });

  const showToast = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const requestSubmitAbort = useCallback(() => {
    cancelRequestedRef.current = true;
    setSubmitState({ step: 'error', progress: 0, message: 'Đã hủy lưu thay đổi' });
    showToast('Đã hủy lưu thay đổi', 'error');
  }, [showToast]);

  // Handle removing an existing image from a defect line
  const handleExistingImageRemove = useCallback((lineIndex: number, imageIndex: number) => {
    setExistingImagesMap((prev) => {
      const updated = new Map(prev);
      const lineImages = updated.get(lineIndex);
      if (lineImages) {
        const filtered = lineImages.filter((_, i) => i !== imageIndex);
        if (filtered.length > 0) {
          updated.set(lineIndex, filtered);
        } else {
          updated.delete(lineIndex);
        }
      }
      return updated;
    });
    // Mark form as dirty since user removed an image
    methods.setValue('notes', methods.getValues('notes'), { shouldDirty: true });
  }, [methods]);

  const handleCancel = useCallback(() => {
    goBack();
  }, [goBack]);

  /**
   * Handle defect line deletion from form.
   * Always shows confirmation dialog. After confirmation:
   * - If line has API name → call delete API then remove from form
   * - If line has no API name (new unsaved) → just remove from form
   */
  const handleDefectLineDelete = useCallback(
    (formIndex: number, removeFn: () => void) => {
      const lineData = methods.getValues(`defectLines.${formIndex}`);
      const apiName = lineData?.apiDefectLineName;

      setDeleteConfirm({
        open: true,
        apiDefectLineName: apiName,
        formIndex,
        removeFn,
      });
    },
    [methods]
  );

  const confirmDeleteDefectLine = useCallback(() => {
    if (!deleteConfirm) return;
    const { apiDefectLineName, removeFn } = deleteConfirm;
    setDeleteConfirm(null);

    if (apiDefectLineName && ticketId) {
      // Existing line from server → call delete API then remove from form
      deleteDefectLineMutation.mutate(
        {
          ticket_id_external: ticketId,
          defect_line_id: apiDefectLineName,
          incident_id: urlParams?.incident_id || '',
        },
        {
          onSuccess: () => {
            removeFn();
            methods.clearErrors();
            showToast('Đã xóa dòng lỗi', 'success');
          },
          onError: () => {
            showToast('Xóa dòng lỗi thất bại', 'error');
          },
        }
      );
    } else {
      // New unsaved line → just remove from form
      removeFn();
      methods.clearErrors();
      showToast('Đã xóa dòng lỗi', 'success');
    }
  }, [deleteConfirm, ticketId, urlParams, deleteDefectLineMutation, showToast, methods]);

  const cancelDeleteDefectLine = useCallback(() => {
    setDeleteConfirm(null);
  }, []);

  const transformToApiLine = (line: CreateTicketFormData['defectLines'][number]): DefectLineInput => ({
    line_type: line.lineType,
    component_item_code: line.componentItemCode || undefined,
    is_other_component: line.isOtherComponent || false,
    other_component_description: line.otherComponentDescription || undefined,
    market_defect: line.marketDefect || undefined,
    is_other_defect: line.isOtherDefect || false,
    other_defect_description: line.otherDefectDescription || undefined,
    market_defect_cause: line.marketDefectCause || undefined,
    is_other_cause: line.isOtherCause || false,
    other_cause_description: line.otherCauseDescription || undefined,
  });

  const getImageFiles = (line: CreateTicketFormData['defectLines'][number]): File[] =>
    (line.images ?? [])
      .map((image) => {
        const maybeFile = image as File | { file: File };
        return maybeFile instanceof File ? maybeFile : maybeFile.file;
      })
      .filter((file): file is File => file instanceof File);

  const handleValidationError = useCallback((_errors: FieldErrors<CreateTicketFormData>) => {
    window.setTimeout(() => {
      const firstInvalidField = document.querySelector<HTMLElement>(
        '[aria-invalid="true"], .Mui-error input, .Mui-error textarea'
      );
      firstInvalidField?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 0);
  }, []);

  const handleSubmit = methods.handleSubmit(async (formData) => {
    cancelRequestedRef.current = false;

    if (!urlParams || !ticketId) {
      showToast('Thiếu thông tin URL params', 'error');
      return;
    }

    const submitProgress = { currentStep: 'validating' as SubmitStep };
    const updateSubmitState = (state: SubmitState) => {
      submitProgress.currentStep = state.step;
      setSubmitState(state);
    };
    const throwIfCancelled = () => {
      if (cancelRequestedRef.current) {
        throw new Error('Đã hủy lưu thay đổi');
      }
    };

    updateSubmitState({ step: 'validating', progress: 10, message: 'Đang kiểm tra dữ liệu...' });

    try {
      // Step 1: Validate
      updateSubmitState({ step: 'validating', progress: 15, message: 'Đang kiểm tra dữ liệu...' });
      await new Promise((resolve) => setTimeout(resolve, 200));
      throwIfCancelled();

      // Build defect lines for update API — include existing `name` for known lines
      const defectLinesForApi: UpdateDefectLineInput[] = formData.defectLines.length === 0
        ? []
        : formData.defectLines.map((line, index) => {
          const apiLine = transformToApiLine(line);
          // Merge existing images
          const existing = existingImagesMap.get(index) || [];
          // Carry over the server-side `name` for existing lines so the API updates (not creates) them
          // Use the apiDefectLineName from form data (stable across add/remove) instead of index-based lookup
          const existingName = line.apiDefectLineName || ticket?.defect_lines[index]?.name;
          return {
            ...apiLine,
            ...(existingName ? { name: existingName } : {}),
            images: existing,
          };
      });

      // Step 2: Update ticket FIRST — this creates server-side IDs for new defect lines
      updateSubmitState({ step: 'updating', progress: 30, message: 'Đang lưu thay đổi...' });

      const updateRequest: UpdateTicketRequest = {
        ticket_id_external: urlParams.incident_id,
        item_code: resolvedItemCode || urlParams.item_code || '',
        serial_no: urlParams.serial_no,
        reported_date: ticket?.reported_date || new Date().toISOString().split('T')[0],
        technician_id: urlParams.technician_id,
        technician_name: urlParams.technician_name,
        customer_name: urlParams.customer_name,
        customer_phone: urlParams.customer_phone,
        customer_address: urlParams.customer_address,
        notes: formData.notes,
        defect_lines: defectLinesForApi,
      };

      const updatedTicket = await updateTicketMutation.mutateAsync(updateRequest);
      throwIfCancelled();

      // Step 3: Upload new images AFTER ticket update (so new lines have server-side names)
      const linesWithNewImages = formData.defectLines
        .map((line, index) => ({
          files: getImageFiles(line),
          index,
        }))
        .filter((line) => line.files.length > 0);

      if (linesWithNewImages.length > 0) {
        updateSubmitState({ step: 'uploading', progress: 55, message: 'Đang tải ảnh lên...' });

        for (let i = 0; i < linesWithNewImages.length; i++) {
          throwIfCancelled();
          const { files, index } = linesWithNewImages[i];
          // Use defect_line name from the UPDATE response (covers both existing & newly created lines)
          const defectLineName = updatedTicket.defect_lines[index]?.name;

          if (!defectLineName) {
            console.warn(`Skipping image upload for line #${index + 1}: no defect_line_name in response`);
            continue;
          }

          const uploadResult = await imageUpload.uploadImages(files, {
            ticketName: updatedTicket.name,
            defectLineName,
          });
          throwIfCancelled();

          console.log(`Uploaded ${uploadResult.uploaded_count} images for line #${index + 1}`);

          const progress = 55 + ((i + 1) / linesWithNewImages.length) * 30;
          updateSubmitState({
            step: 'uploading',
            progress,
            message: `Đang tải ảnh lên... (${i + 1}/${linesWithNewImages.length} dòng)`,
          });
        }
      }
      throwIfCancelled();

      // Invalidate ticket detail cache to force refetch with updated images.
      // The mutation's onSuccess already set query data, but that was BEFORE
      // images were uploaded. This ensures the Detail page gets fresh data.
      await invalidateTicketDetail(queryClient, urlParams.incident_id);

      // Step 4: Success
      updateSubmitState({ step: 'success', progress: 100, message: 'Hoàn tất!' });
      showToast('✅ Lưu thay đổi thành công', 'success');

      // Reset form dirty state to prevent unsaved changes modal from appearing
      methods.reset(formData, { keepValues: true });

      setTimeout(() => {
        if (cancelRequestedRef.current) return;
        hasNavigatedRef.current = true;
        navigate(generatePath(ROUTES.DETAIL, { ticketId }));
        triggerDeepLink({ action: 'updated', ticket_id: ticketId });
      }, 800);
    } catch (error) {
      if (cancelRequestedRef.current) {
        setSubmitState({ step: 'error', progress: 0, message: 'Đã hủy lưu thay đổi' });
        showToast('Đã hủy lưu thay đổi', 'error');
        return;
      }

      // Extract API error message from Error or AppError (plain object from normalizeError)
      const apiMessage =
        error instanceof Error
          ? error.message
          : (error && typeof error === 'object' && 'message' in error)
            ? (error as { message: string }).message
            : null;

      let errorMessage: string;
      if (submitProgress.currentStep === 'uploading') {
        errorMessage = 'Tải ảnh lên thất bại';
      } else {
        errorMessage = 'Cập nhật báo cáo thất bại';
      }

      // Append API message if available
      if (apiMessage) {
        errorMessage = `${errorMessage}: ${apiMessage}`;
      }

      setSubmitState({ step: 'error', progress: 0, message: errorMessage });
      showToast(errorMessage, 'error');
      console.error('Update ticket error:', error);
    }
  }, handleValidationError);

  const isBusy = submitState.step !== 'idle' && submitState.step !== 'error' && submitState.step !== 'success';
  const hasValidationErrors = methods.formState.isSubmitted && Object.keys(methods.formState.errors).length > 0;

  const fetchErrorMessage = (() => {
    if (fetchError instanceof Error) return fetchError.message;
    return 'Không thể tải chi tiết báo cáo';
  })();

  const getBackdropContent = () => {
    if (submitState.step === 'success') {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
            Thành công!
          </Typography>
          <Typography sx={{ color: '#fff' }}>
            Đang chuyển hướng...
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', width: 300 }}>
        <CircularProgress color="inherit" sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
          Đang lưu thay đổi...
        </Typography>
        <Typography sx={{ mb: 2, color: '#fff' }}>
          {submitState.message}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={submitState.progress}
          sx={{
            width: '100%',
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#fff',
            },
          }}
        />
        <Button
          variant="outlined"
          color="inherit"
          onClick={requestSubmitAbort}
          sx={{ mt: 2 }}
        >
          Hủy
        </Button>
      </Box>
    );
  };

  // Loading state while fetching ticket data
  if (isLoadingTicket) {
    return (
      <AppLayout
        header={
          <AppHeader
            title="Chỉnh sửa báo cáo lỗi"
            onBack={goBack}
          />
        }
      >
        <Box sx={{ p: 2 }}>
          <DetailSkeleton />
        </Box>
      </AppLayout>
    );
  }

  // Error state fetching ticket data
  if (isError) {
    return (
      <AppLayout
        header={
          <AppHeader
            title="Chỉnh sửa báo cáo lỗi"
            onBack={goBack}
          />
        }
      >
        <Box sx={{ p: 2 }}>
          <ErrorBanner
            message={fetchErrorMessage}
            onRetry={() => refetch()}
          />
        </Box>
      </AppLayout>
    );
  }

  return (
    <FormProvider {...methods}>
      <AppLayout
        header={
          <AppHeader
            title="Chỉnh sửa báo cáo lỗi"
            onBack={isBusy ? undefined : handleCancel}
          />
        }
        bottomBar={
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              px: 2,
              py: 1,
              height: '100%',
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              disabled={isBusy}
            >
              Hủy
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={isBusy}
            >
              Lưu thay đổi
            </Button>
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <ProductInfoCard />
          {hasValidationErrors && (
            <Alert severity="error" sx={{ mb: 2 }} data-error-summary="true">
              Vui lòng kiểm tra lại các thông tin bắt buộc trước khi lưu.
            </Alert>
          )}
          <DefectLinesList
            disabled={isBusy}
            existingImagesMap={existingImagesMap}
            onExistingImageRemove={handleExistingImageRemove}
            onDeleteDefectLine={handleDefectLineDelete}
            minLines={0}
          />
          <NotesTextarea disabled={isBusy} />
        </Box>
      </AppLayout>

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        open={showModal}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}
        open={isBusy || submitState.step === 'success'}
      >
        {getBackdropContent()}
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Defect Line Confirmation Dialog */}
      <Dialog
        open={deleteConfirm?.open ?? false}
        onClose={cancelDeleteDefectLine}
      >
        <DialogTitle>Xác nhận xóa dòng lỗi</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa dòng lỗi này? Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteDefectLine} disabled={deleteDefectLineMutation.isPending}>
            Hủy
          </Button>
          <Button
            onClick={confirmDeleteDefectLine}
            color="error"
            variant="contained"
            disabled={deleteDefectLineMutation.isPending}
            startIcon={deleteDefectLineMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {deleteDefectLineMutation.isPending ? 'Đang xóa...' : 'Xóa'}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
